// The SETTINGS panels, in the prototype's card vocabulary (app.css .set-card/.kv/.notify-row/.tswitch):
// the value-free deployment-settings inventory grouped for reading, and the notify-me settings rendered as
// INFORMATIONAL rows with the prototype's toggle VISUAL showing the deployment default. The inventory rows
// are key/purpose/default/secret-flag ONLY — NEVER a runtime value (G-173), so a secret setting shows its
// existence and purpose but no value can leak (G-140). The notify rows are DEPLOYMENT settings whose
// posture an Operator sets in config: Ritona stores no per-user notify preference (BUILD-DEC-085 #6,
// Option A), so the row states the DEFAULT posture and routes nothing (P-SELF — the toggle visual is a
// read-only state marker here, not a control). Tokens only (G-181).

import { EmptyState, Panel, StatusPill } from '../primitives.tsx';
import { KeyValue } from './know-primitives.tsx';
import type { SettingRow } from '../know-projections.ts';

/** Group the flat inventory into the surface's sections by a coarse purpose prefix on the setting key. */
function sectionOf(key: string): 'Region & time' | 'Data & privacy' | 'Sessions' | 'Configuration' {
  const lower = key.toLowerCase();
  if (lower.includes('zone') || lower.includes('region') || lower.includes('time')) {
    return 'Region & time';
  }
  if (lower.includes('privacy') || lower.includes('data') || lower.includes('retention')) {
    return 'Data & privacy';
  }
  if (lower.includes('session') || lower.includes('sso') || lower.includes('auth')) {
    return 'Sessions';
  }
  return 'Configuration';
}

export interface SettingsInventoryProps {
  readonly settings: readonly SettingRow[];
}

/** The value-free settings inventory as prototype set-cards (no runtime value shown). */
export function SettingsInventory({ settings }: SettingsInventoryProps): React.JSX.Element {
  if (settings.length === 0) {
    return (
      <Panel>
        <EmptyState>No configuration settings to show.</EmptyState>
      </Panel>
    );
  }
  const sections = ['Region & time', 'Data & privacy', 'Sessions', 'Configuration'] as const;
  return (
    <div className="set-col" data-settings-inventory>
      {sections.map((section) => {
        const rows = settings.filter((row) => sectionOf(row.key) === section);
        if (rows.length === 0) return null;
        return (
          <div key={section} className="set-card">
            <div className="h-section">{section}</div>
            <div className="kv-list" data-settings-section={section}>
              {rows.map((row) => (
                <KeyValue key={row.key} label={row.purpose}>
                  {row.secret ? (
                    <StatusPill tone="neutral">Secret · not shown</StatusPill>
                  ) : (
                    <span className="mono-label" style={{ textTransform: 'none' }}>
                      {row.defaultText}
                    </span>
                  )}
                </KeyValue>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export interface NotifyDefaultProps {
  readonly settingKey: string;
  readonly title: string;
  /** The deployment DEFAULT posture — there is no per-user store to override it (BUILD-DEC-085 #6, Option A). */
  readonly on: boolean;
}

/**
 * One notify setting rendered as an INFORMATIONAL row (prototype .notify-row) — NOT an interactive control.
 * Ritona stores no per-user notify preference (BUILD-DEC-085 #6, Option A) and the settings inventory is
 * value-free (G-173): these are DEPLOYMENT settings whose posture an Operator sets in config, not something
 * proposed from the dashboard. The prototype toggle renders as a read-only state marker (P-SELF).
 */
export function NotifyDefault({ settingKey, title, on }: NotifyDefaultProps): React.JSX.Element {
  return (
    <div
      data-notify-setting={settingKey}
      data-notify-default={on ? 'on' : 'off'}
      className="notify-row"
      style={{ cursor: 'default' }}
    >
      <span className="nr-body">
        <span className="nr-title">{title}</span>
        <span className="nr-sub">
          {on ? 'on by default' : 'off by default'} · set per deployment
        </span>
      </span>
      <span className={on ? 'tswitch on' : 'tswitch'} aria-hidden="true">
        <span className="kn" />
      </span>
    </div>
  );
}
